import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import publicData from '../../../data/publicData.json';

const ToolsWeUseSection = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const [selectedCategory, setSelectedCategory] = useState(0);
  const { toolsWeUse } = publicData;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const slideTransition = {
    duration: 0.5,
    ease: [0.43, 0.13, 0.23, 0.96]
  };

  const handleCategoryClick = (index) => {
    setSelectedCategory(index);
  };

  return (
    <section 
      id="tools" 
      className={`py-24 ${
        isDark 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-50 via-gray-100 to-white'
      }`}
    >
      <motion.div
        className="container mx-auto px-4 max-w-8xl"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-10"
          variants={itemVariants}
        >
          <h2 className={`text-4xl font-bold mb-8 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {toolsWeUse.title}
          </h2>
          <p className={`text-lg mb-12  ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {toolsWeUse.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Categories List */}
          <motion.div 
            className="space-y-2 max-h-[500px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-transparent"
            variants={containerVariants}
          >
            {toolsWeUse.categories.map((category, index) => (
              <motion.div
                key={index}
                className={`p-2 rounded-lg cursor-pointer transition-all duration-300 ${
                  selectedCategory === index
                    ? isDark
                      ? 'bg-blue-900/30 shadow-md shadow-blue-500/20'
                      : 'bg-blue-100 shadow-md shadow-blue-500/20'
                    : isDark
                      ? 'bg-gray-800/50 hover:bg-gray-800/70'
                      : 'bg-white hover:bg-gray-50'
                } backdrop-blur-sm`}
                variants={itemVariants}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => handleCategoryClick(index)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    selectedCategory === index
                      ? isDark ? 'bg-blue-800/50' : 'bg-blue-200'
                      : isDark ? 'bg-gray-700/50' : 'bg-gray-100'
                  }`}>
                    <div className={`${
                      selectedCategory === index
                        ? isDark ? 'text-blue-400' : 'text-blue-600'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={category.icon}
                        />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className={`text-xl font-semibold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {category.title}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Tools Display */}
          <div className="relative h-[500px] overflow-hidden">
            <AnimatePresence initial={false} custom={selectedCategory}>
              <motion.div
                key={selectedCategory}
                custom={selectedCategory}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={slideTransition}
                className={`absolute inset-0 p-4 rounded-lg ${
                  isDark
                    ? 'bg-gray-800/50'
                    : 'bg-white'
                } backdrop-blur-sm shadow-lg overflow-y-auto scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-transparent`}
              >
                <div className="space-y-4">
                  {toolsWeUse.categories[selectedCategory].tools.map((tool, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <h4 className={`text-lg font-semibold mb-2 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {tool.name}
                      </h4>
                      <div className="flex flex-wrap gap-1.5">
                        {tool.items.map((item, itemIdx) => (
                          <motion.span
                            key={itemIdx}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: (idx * tool.items.length + itemIdx) * 0.05 }}
                            className={`text-sm px-2 py-1 rounded-full ${
                              isDark 
                                ? 'bg-blue-900/30 text-blue-400'
                                : 'bg-blue-100 text-blue-600'
                            }`}
                          >
                            {item}
                          </motion.span>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Benefits Section */}
        <motion.div 
          className="mt-16 text-center"
          variants={containerVariants}
        >
          <motion.h3 
            className={`text-2xl font-bold mb-8  ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
            variants={itemVariants}
          >
            Why Our Tools Matter?
          </motion.h3>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={containerVariants}
          >
            {toolsWeUse.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className={`p-6 rounded-xl transition-all duration-300 ${
                  isDark
                    ? 'bg-gray-800/50'
                    : 'bg-white'
                } backdrop-blur-sm shadow-lg`}
                variants={itemVariants}
              >
                <p className={`text-sm font-semibold mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {benefit}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-12"
          variants={itemVariants}
        >
          <Link
            to={toolsWeUse.cta.link}
            className={`inline-flex items-center px-6 py-3 rounded-lg text-base font-semibold transition-all duration-300 ${
              isDark 
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            } transform hover:scale-105 active:scale-95`}
          >
            {toolsWeUse.cta.text}
            <FaArrowRight className="ml-2" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ToolsWeUseSection; 